import { formatPlanningBufferLabel, isPlanningBufferConfigured, NO_BUFFER_SET_LABEL } from '@/utils/planningBuffer';
import { TicketReconstructedHistory } from './TicketReconstructedHistory';
import { EFFICIENCY_NAMES } from '@/lib/efficiencyNames'; // CORE-G GAP-005: one name, one meaning
import { useEffect, useMemo, useRef, useState } from 'react';
import { ShowFeature } from '@/components/access/ShowFeature';
import { useExposureFlag } from '@/hooks/useFeatureFlags';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CommandCenterTabBoundary } from '@/components/tickets/CommandCenterTabBoundary';
import { TicketProvenanceBadge } from '@/components/tickets/TicketProvenanceBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TicketLifecycleTimeCard } from './TicketLifecycleTimeCard';
import { 
  Clock, 
  Car, 
  User, 
  Phone, 
  Wrench, 
  Calendar, 
  Timer, 
  AlertTriangle,
  RotateCcw,
  ParkingSquare,
  MapPin,
  MessageSquare,
  BarChart3,
  GraduationCap,
  Stethoscope,
  Pencil,
  Plus,
  Package
} from 'lucide-react';
import { WorkflowReportTab } from './WorkflowReportTab';
import { TicketPartsPanel } from './parts/TicketPartsPanel';
import { InvoicingCard } from '@/components/invoicing/InvoicingCard';
import { TicketBusinessStateBadge } from './TicketBusinessStateBadge';
import { TicketBusinessStateTimeline } from './TicketBusinessStateTimeline';
import { ReadyToInvoiceBlockersList } from './ReadyToInvoiceBlockersList';
import { TicketCloseoutPanel } from './TicketCloseoutPanel';
import { useTicketBusinessState } from '@/hooks/tickets/useTicketBusinessState';
import { AddJobDialog } from './AddJobDialog';
import { resolveAddJobSurface } from '@/lib/tickets/addJobSurface';
import { TicketRankingPanel } from './TicketRankingPanel';
import { TicketTrainingTab } from './TicketTrainingTab';
import { OptiBayDiagnosticsTab } from '@/components/optibay/OptiBayDiagnosticsTab';
import { EditCustomerVehicleModal } from './EditCustomerVehicleModal';
import { EditTicketDetailsModal } from './EditTicketDetailsModal';
import { Ticket } from '@/types';
import { formatShopTime } from '@/utils/formatShopTime';
import { useShopTime } from '@/hooks/useShopTime';
import { usePermissions } from '@/hooks/usePermissions';
import { OptiVehicleProfileCard } from '@/components/vehicle-profile/OptiVehicleProfile';
import { useAuth } from '@/contexts/AuthContext';
import { TicketHandoffContext } from '@/components/lifecycle/TicketHandoffContext';
import { TicketOriginLine } from '@/components/tickets/TicketOriginLine';
import { TicketDepartureRecord } from './TicketDepartureRecord';
// Core-100 R4 X4 (CORE-AE F / GAP-005): the location & custody section, and each tab's freshness stamp.
import { TicketLocationCustodyPanel } from '@/components/tickets/TicketLocationCustodyPanel';
import { CommandCenterFreshness } from '@/components/tickets/CommandCenterFreshness';
import { formatEngineSizeDisplay } from '../../../supabase/functions/_shared/vehicleProfile/engineDisplay';
import { useVehicleIdentity } from '@/hooks/useVehicleIdentity';
import { formatTicketNumber } from '@/lib/tickets/ticketDisplayNumber';
import { cardPlateLabel, cardVinLabel } from '@/lib/dayboard/cardTimerRunState';

interface TicketDetailsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: Ticket;
  technician?: {
    name: string;
    efficiencyPercentage?: number;
    bufferPercentage?: number;
  };
  ticketJobs: Array<{
    id: string;
    name: string;
    standardMinutes: number;
    category?: string;
  }>;
  elapsedSeconds: number;
  calculateOptiTime: number;
  calculateOptiFinish: string;
  totalMinutes: number;
  bayName?: string | null;
  /** Oct 20 Issue 06 deep link: the tab to show when the Command Center opens (unset keeps the last tab). */
  initialTab?: string;
  /** …and the section of that tab to bring into view (Workflow → Internal Notes). */
  focusSection?: 'notes';
}

export const TicketDetailsPopup = ({
  isOpen,
  onClose,
  ticket,
  technician,
  ticketJobs,
  elapsedSeconds,
  calculateOptiTime,
  calculateOptiFinish,
  totalMinutes,
  bayName,
  initialTab,
  focusSection,
}: TicketDetailsPopupProps) => {
  const { shopTimezone } = useShopTime();
  const { canViewVehicleIdentity, canViewCustomerContact, canViewRankingTab, canEditTickets } = usePermissions();
  const { user, activeShop } = useAuth();
  const queryClient = useQueryClient();

  // The ticket arrives as props from the board, which refreshes on every
  // Stop / Final Stop / pickup — but the Command Center's own panels
  // (lifecycle jobs, business state, commercial closeout, invoice readiness)
  // are separate queries that nothing invalidated, so an open Command Center
  // kept showing the pre-Stop answer. When the ticket's production facts
  // change, re-read them (the same keys the business-state mutations use).
  const productionFacts = [
    ticket?.status, (ticket as any)?.timer_status, (ticket as any)?.completed_at, (ticket as any)?.picked_up_at,
  ].join('|');
  const lastProductionFacts = useRef(productionFacts);
  useEffect(() => {
    if (lastProductionFacts.current === productionFacts) return;
    lastProductionFacts.current = productionFacts;
    for (const key of ['ticket-business-state', 'ticket-business-state-history', 'ready-to-invoice-eligibility',
      'ticket-commercial-closeout', 'ticket-jobs-for-lifecycle']) {
      void queryClient.invalidateQueries({ queryKey: [key, ticket?.id] });
    }
  }, [productionFacts, ticket?.id, queryClient]);

  // Core-100 R4 X4 (CORE-AE GAP-005): the tab in view says when its data was read; Refresh re-reads it.
  const [activeTab, setActiveTab] = useState('details');
  // Deep link: each time the dialog opens WITH a target, select that tab and ask it to focus the section. Opening
  // without one (the ticket number) keeps the existing sticky-tab behaviour.
  const [focusRequest, setFocusRequest] = useState(0);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current && initialTab) {
      setActiveTab(initialTab);
      if (focusSection) setFocusRequest((n) => n + 1);
    }
    wasOpen.current = isOpen;
  }, [isOpen, initialTab, focusSection]);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const refreshSection = () => {
    void queryClient.invalidateQueries({ predicate: (q) => q.queryKey.some((k) => k === ticket?.id) });
    setRefreshNonce((n) => n + 1);
  };
  const TAB_SECTION_NAMES: Record<string, string> = {
    details: 'Details', vehicle: 'Vehicle', location: 'Location & custody', workflow: 'Workflow', diagnostics: 'Diagnostics',
    training: 'Training', parts: 'Parts', history: 'History', ranking: 'Ranking',
  };

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditDetailsOpen, setIsEditDetailsOpen] = useState(false);
  const [isAddJobOpen, setIsAddJobOpen] = useState(false);
  // Phase 4A: when ON, Jobs section moves to the Parts & Labor tab and the
  // Details-tab Jobs block is hidden (kept temporarily as fallback when OFF).
  const partsAndLaborEnabled = useExposureFlag('ui_partsAndLabor');
  // FLOYD-1: the re-home flag alone is not enough to hide Jobs. The tab they
  // move INTO is gated separately, and the two flags are independent — so
  // `ui_partsAndLabor ON` with `ui_ticketPartsPanel OFF` used to hide the
  // section and its replacement at once. One decision, asked by both surfaces.
  const ticketPartsPanelEnabled = useExposureFlag('ui_ticketPartsPanel');
  const addJobSurface = resolveAddJobSurface({
    partsAndLaborEnabled,
    ticketPartsPanelEnabled,
  });

  // Resolve current user's technicianProfileId for training event tracking (Phase 3)
  const { data: techProfileData } = useQuery({
    queryKey: ['tech-profile-for-training', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('technician_profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) return null;
      return data?.id ?? null;
    },
    enabled: !!user?.id,
    staleTime: 30 * 60 * 1000, // 30 min — stable, rarely changes
  });
  const technicianProfileId = techProfileData ?? null;

  // OD-34 (founder ruling 2026-10-13): technicians and detail specialists READ the customer's contact on every
  // ticket of their shop (the server already returns it to them). Reading is not editing — the Customer & Vehicle
  // Edit button is the front office's (edit_ticket_customer_contact refuses everyone else). VIN / plate display
  // keeps its own rule (canViewTicketPII) — not part of OD-34.
  const showContact = canViewCustomerContact;
  // OCT28 #1: VIN / plate follow canViewVehicleIdentity (PII roles + technicians); contact stays on OD-34 above.
  const showPII = canViewVehicleIdentity;

  // Format elapsed time as HH:MM:SS
  const formatElapsedTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Format minutes to readable format
  const formatMinutes = (minutes: number): string => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs > 0) {
      return `${hrs}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Get ticket number
  // NOV3 A6: the repair's number (the chain head's for every rollover day).
  const ticketNumber = useMemo(() => formatTicketNumber(ticket as any).replace(/^#(TKT-)?/, ''), [ticket]);

  // Format start time
  const formattedStartTime = useMemo(() => {
    try {
      if ((ticket as any).actually_started && (ticket as any).actual_start_time) {
        return formatShopTime(new Date((ticket as any).actual_start_time), shopTimezone, 'short');
      }
      // Use raw UTC start_time from DB, not the pre-formatted startTime
      const rawStartTime = (ticket as any).start_time;
      if (rawStartTime) {
        return formatShopTime(new Date(rawStartTime), shopTimezone, 'short');
      }
      // Fallback: if startTime is already a formatted display string, use it directly
      if (ticket.startTime && ticket.startTime !== 'Not set') {
        return ticket.startTime;
      }
      return 'Not set';
    } catch {
      return 'Not set';
    }
  }, [ticket, shopTimezone]);

  // Format due out time
  const formattedDueOut = useMemo(() => {
    try {
      const dueOut = (ticket as any).due_out || ticket.dueOut;
      if (dueOut) {
        return formatShopTime(new Date(dueOut), shopTimezone, 'short');
      }
      return 'Not set';
    } catch {
      return 'Not set';
    }
  }, [ticket, shopTimezone]);

  // Get vehicle info
  const vehicle = ticket.vehicle;
  // OCT28 RECOVERY #1: the stored VIN / plate are encrypted envelopes; show the server-decrypted identity (same
  // batched, vehicle-keyed read as the card), and never an envelope.
  const { identity: vehicleIdentity } = useVehicleIdentity(
    (ticket as any).shop_id ?? activeShop?.id,
    (vehicle as any)?.id ?? (ticket as any).vehicle_id ?? null,
    showPII,
  );
  const identityVin = cardVinLabel(vehicleIdentity?.vin ?? (vehicle as any)?.vin);
  const identityPlate = vehicleIdentity?.license_plate ?? (vehicle as any)?.licensePlate ?? null;
  const identityPlateShown = cardPlateLabel(identityPlate, null) ? identityPlate : null;
  const vehicleEngineSize = (vehicle as any)?.engineSize || (vehicle as any)?.engine_size || '';
  const vehicleDisplay = vehicle 
    ? `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim() || 'Unknown Vehicle'
    : 'Unknown Vehicle';

  // Get customer info
  const customerName = (ticket as any).customer_name || ticket.customerName || 'Unknown Customer';
  const customerPhone = (ticket as any).customer_phone || ticket.customerPhone;

  // Get ticket type
  const ticketType = (ticket as any).waiting_type || ticket.type || 'Drop-off';

  // Status badge styling
  const getStatusBadgeStyle = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-500/10 text-green-600 border-green-500/20';
      case 'Paused':
        return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
      case 'Completed':
        return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
      case 'Blocked':
        return 'bg-red-500/10 text-red-600 border-red-500/20';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[1000px] max-h-[85vh] max-sm:h-[85vh] p-0 flex flex-col overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-semibold">
              Ticket #{ticketNumber} Command Center
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={getStatusBadgeStyle(ticket.status)}>
                {ticket.status}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {ticketType}
              </Badge>
              {(ticket as any).is_comeback && (
                <Badge variant="destructive" className="gap-1">
                  <RotateCcw className="h-3 w-3" />
                  Comeback
                </Badge>
              )}
              {(ticket as any).parking_spot?.label && (
                <Badge variant="secondary" className="gap-1 text-xs">
                  <ParkingSquare className="h-3 w-3" />
                  {(ticket as any).parking_spot.label.replace(/^Parking\s*/i, 'Parking Spot ')}
                </Badge>
              )}
            </div>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className={`grid w-full h-auto grid-cols-3 ${canViewRankingTab ? 'sm:grid-cols-8' : 'sm:grid-cols-7'} mx-6 mb-2`} style={{ width: 'calc(100% - 3rem)' }}>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="vehicle" data-testid="command-center-vehicle-tab">
              <Car className="h-4 w-4 mr-2" />
              Vehicle
            </TabsTrigger>
            <TabsTrigger value="location" data-testid="command-center-location-tab">
              <MapPin className="h-4 w-4 mr-2" />
              Location
            </TabsTrigger>
            <TabsTrigger value="workflow" data-testid="command-center-workflow-tab">
              <MessageSquare className="h-4 w-4 mr-2" />
              Workflow
            </TabsTrigger>
            <ShowFeature feature="ui_evidenceDiagnostics">
              <TabsTrigger value="diagnostics">
                <Stethoscope className="h-4 w-4 mr-2" />
                Diagnostics
              </TabsTrigger>
            </ShowFeature>
            <TabsTrigger value="training">
              <GraduationCap className="h-4 w-4 mr-2" />
              Training
            </TabsTrigger>
            <ShowFeature feature="ui_ticketPartsPanel">
              <TabsTrigger value="parts">
                <Package className="h-4 w-4 mr-2" />
                {partsAndLaborEnabled ? 'Parts & Labor' : 'Parts'}
              </TabsTrigger>
            </ShowFeature>
            <TabsTrigger value="history" data-testid="command-center-history-tab">
              History
            </TabsTrigger>
            {canViewRankingTab && (
              <TabsTrigger value="ranking">
                <BarChart3 className="h-4 w-4 mr-2" />
                Ranking
              </TabsTrigger>
            )}
          </TabsList>
          
          <CommandCenterFreshness
            key={`${activeTab}-${refreshNonce}`}
            ticketId={ticket.id}
            section={TAB_SECTION_NAMES[activeTab] ?? activeTab}
            ticketUpdatedAt={(ticket as { updated_at?: string | null }).updated_at ?? null}
            timeZone={shopTimezone}
            onRefresh={refreshSection}
          />

          <TabsContent value="details" className="flex-1 mt-0 overflow-hidden">
            <CommandCenterTabBoundary key={`details-${refreshNonce}`} section="Details" ticketId={ticket.id}>
              <ScrollArea className="h-[calc(85vh-160px)]">
                <div className="px-6 pb-6 space-y-5">
                  {/* Why the front office has this ticket — the latest technician
                      handoff (reason, note, who, when) and, while it is still
                      pending, the same Accept the FOH queue performs. */}
                  <TicketHandoffContext ticketId={ticket.id} />
                  {/* Core-100 R4 W4 (CORE-Y): how this ticket came to exist (tickets.creation_method) */}
                  <TicketOriginLine ticketId={ticket.id} />
                  {/* Oct 20 addendum: picked up — work incomplete (reason, expected return, return status, history) */}
                  <TicketDepartureRecord ticketId={ticket.id} timeZone={shopTimezone} />

                  {/* Customer & Vehicle Section */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                        Customer & Vehicle
                      </h3>
                      {canEditTickets && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 gap-1.5 text-xs"
                          onClick={() => setIsEditModalOpen(true)}
                          data-testid="command-center-edit-customer"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Edit
                        </Button>
                      )}
                    </div>
                    <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <User className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="font-medium" data-testid="command-center-customer-name">
                          {showContact ? customerName : '••• (Redacted)'}
                        </span>
                      </div>
                      {customerPhone && showContact && (
                        <div className="flex items-center gap-3">
                          <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-muted-foreground" data-testid="command-center-customer-phone">{customerPhone}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-3">
                        <Car className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span>{vehicleDisplay}{vehicleEngineSize ? ` • ${formatEngineSizeDisplay(vehicleEngineSize)}` : ''}</span>
                      </div>
                      {identityVin && showPII && (
                        <div className="flex items-center gap-3 pl-7">
                          {/* OCT28 #1: the VIN opens THIS ticket's vehicle record (the Vehicle tab, keyed by vehicle_id). */}
                          <button
                            type="button"
                            className="text-xs text-primary underline-offset-2 hover:underline font-mono"
                            data-testid="command-center-vin-link"
                            onClick={() => setActiveTab('vehicle')}
                          >
                            VIN: {identityVin}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* Timing Section */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                        Timing
                      </h3>
                      {/* Core-100 R4 X4: ticket details are front-office data (edit_ticket_details refuses others). */}
                      {canEditTickets && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          data-testid="command-center-edit-details"
                          onClick={() => setIsEditDetailsOpen(true)}
                        >
                          <Pencil className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-muted/30 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Start Time</span>
                        </div>
                        <span className="text-lg font-semibold">{formattedStartTime}</span>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Due Out</span>
                        </div>
                        <span className="text-lg font-semibold">{formattedDueOut}</span>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Book Time</span>
                        </div>
                        <span className="text-lg font-semibold">{formatMinutes(totalMinutes)}</span>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Timer className="h-4 w-4 text-primary" />
                          <span className="text-xs text-muted-foreground">Tech OptiTime</span>
                        </div>
                        <span className="text-lg font-semibold text-primary">{formatMinutes(calculateOptiTime)}</span>
                      </div>
                    </div>
                  
                    {/* Elapsed & OptiFinish - Full Width */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Timer className="h-4 w-4 text-primary" />
                          <span className="text-xs text-muted-foreground">Elapsed Time</span>
                          <TicketProvenanceBadge factGroupId="ticket.elapsed" />
                        </div>
                        <span className="text-xl font-mono font-bold text-primary">
                          {formatElapsedTime(elapsedSeconds)}
                        </span>
                      </div>
                      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Clock className="h-4 w-4 text-primary" />
                          <span className="text-xs text-muted-foreground">OptiFinish</span>
                          {/* Core-100 R4 (CORE-AE GAP-001): a derivation, beside a measurement — told apart */}
                          <TicketProvenanceBadge factGroupId="ticket.optiFinish" />
                        </div>
                        <span className="text-xl font-semibold text-primary">{calculateOptiFinish}</span>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Slice 5A.2: lifecycle time card. Internally gated by */}
                  {/* `custodyHandoff`; renders nothing when the flag is off, */}
                  {/* so the popup is identical to its pre-slice appearance. */}
                  {/* Display-only — does NOT influence Chronomancer or */}
                  {/* tech bay scheduling. */}
                  <TicketLifecycleTimeCard
                    ticketId={ticket.id}
                    optiTimeMinutes={calculateOptiTime}
                    createdAt={ticket.createdAt ?? null}
                    terminalAt={
                      ticket.status === 'Completed'
                        ? ((ticket as { completed_at?: string | null })
                            .completed_at ?? null)
                        : null
                    }
                    isComeback={ticket.isComeback ?? false}
                  />

                  <Separator />

                  {/* Assignment Section */}
                  {(technician || bayName) && (
                    <>
                      <div className="space-y-3">
                        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                          Assignment
                        </h3>
                        <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                          {technician && (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">{technician.name}</span>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                {technician.efficiencyPercentage && (
                                  <span title={EFFICIENCY_NAMES.planning.scope}>{EFFICIENCY_NAMES.planning.short}: {technician.efficiencyPercentage}%</span>
                                )}
                                <span>{isPlanningBufferConfigured(technician.bufferPercentage) ? `Buf: ${formatPlanningBufferLabel(technician.bufferPercentage)}` : NO_BUFFER_SET_LABEL}</span>
                              </div>
                            </div>
                          )}
                          {bayName && (
                            <div className="flex items-center gap-3">
                              <MapPin className="h-4 w-4 text-muted-foreground" />
                              <span>Bay: {bayName}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <Separator />
                    </>
                  )}

                  {/* Jobs Section — Phase 4A re-homed this to the Parts & Labor
                      tab. FLOYD-1: it is hidden here only when that tab is
                      actually reachable, so Add Job can never fall between the
                      two independent flags. See lib/tickets/addJobSurface. */}
                  {addJobSurface === 'details' && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                          Jobs ({ticketJobs.length})
                        </h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          data-testid="command-center-add-job"
                          onClick={() => setIsAddJobOpen(true)}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Job
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {ticketJobs.length > 0 ? (
                          ticketJobs.map((job, index) => (
                            <div
                              key={job.id || index}
                              className="bg-muted/30 rounded-lg p-3 flex items-center justify-between"
                            >
                              <div className="flex items-center gap-3">
                                <Wrench className="h-4 w-4 text-muted-foreground shrink-0" />
                                <div>
                                  <span className="font-medium text-sm">{job.name}</span>
                                  {job.category && (
                                    <span className="text-xs text-muted-foreground ml-2">
                                      ({job.category})
                                    </span>
                                  )}
                                </div>
                              </div>
                              <Badge variant="secondary" className="font-mono text-xs">
                                {formatMinutes(job.standardMinutes)}
                              </Badge>
                            </div>
                          ))
                        ) : (
                          <div className="text-center text-muted-foreground py-4">
                            No jobs assigned
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Priority */}
                  {ticket.priority !== 'Normal' && (
                    <>
                      <Separator />
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-yellow-500" />
                          <Badge 
                            variant={ticket.priority === 'Emergency' ? 'destructive' : 'secondary'}
                          >
                            {ticket.priority} Priority
                          </Badge>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Phase 16 — Commercial workflow state (separate from operational status) */}
                  <Separator />
                  <Phase16BusinessStateSection ticketId={ticket.id} />

                  {/* Phase 11 — Customer Invoicing & Estimates (self-gates on flag + permissions) */}
                  <InvoicingCard ticketId={ticket.id} />

                  {/* Phase 16 — Sealed commercial closeout (only renders when sealed) */}
                  <TicketCloseoutPanel ticketId={ticket.id} />
                </div>
              </ScrollArea>
            </CommandCenterTabBoundary>
          </TabsContent>
          
          {/* OptiVehicle Profile — the long-lived operational home of the profile; the SAME
              component and data Create Ticket shows (no Command-Center-only vehicle view). */}
          <TabsContent value="vehicle" className="flex-1 overflow-hidden mt-0">
            <CommandCenterTabBoundary key={`vehicle-${refreshNonce}`} section="Vehicle" ticketId={ticket.id}>
              <ScrollArea className="h-[calc(85vh-160px)]">
                <div className="px-6 py-4" data-testid="command-center-vehicle">
                  <OptiVehicleProfileCard
                    shopId={(ticket as any).shop_id ?? activeShop?.id ?? ''}
                    vehicleId={(ticket as any).vehicle_id ?? null}
                    ticketId={ticket.id}
                    vin={showPII ? identityVin : null}
                    plate={showPII ? identityPlateShown : null}
                    canEdit={canEditTickets}
                    context="command"
                  />
                </div>
              </ScrollArea>
            </CommandCenterTabBoundary>
          </TabsContent>

          {/* Core-100 R4 X4 (CORE-AE F): location truth + custody, composed (never owned) — each fact says how it is known. */}
          <TabsContent value="location" className="flex-1 overflow-hidden mt-0">
            <CommandCenterTabBoundary key={`location-${refreshNonce}`} section="Location & custody" ticketId={ticket.id}>
              <TicketLocationCustodyPanel
                ticketId={ticket.id}
                shopId={(ticket as any).shop_id ?? activeShop?.id ?? null}
                timeZone={shopTimezone}
                className="h-[calc(85vh-200px)]"
              />
            </CommandCenterTabBoundary>
          </TabsContent>

          {/* A6: on a phone the tab list wraps to three rows, so the fixed desktop height clipped the bottom of the
              Workflow tab (Internal Notes included). Below sm the tab fills the remaining dialog height instead. */}
          <TabsContent value="workflow" className="flex-1 overflow-hidden mt-0 min-h-0 max-sm:flex max-sm:flex-col">
            <CommandCenterTabBoundary key={`workflow-${refreshNonce}`} section="Workflow" ticketId={ticket.id}>
              <WorkflowReportTab
                ticket={ticket}
                className="h-[calc(85vh-160px)] max-sm:h-auto max-sm:flex-1 max-sm:min-h-0"
                focusSection={activeTab === 'workflow' ? focusSection : undefined}
                focusRequest={focusRequest}
              />
            </CommandCenterTabBoundary>
          </TabsContent>

          <ShowFeature feature="ui_evidenceDiagnostics">
            <TabsContent value="diagnostics" className="flex-1 overflow-hidden mt-0">
              <CommandCenterTabBoundary key={`diagnostics-${refreshNonce}`} section="Diagnostics" ticketId={ticket.id}>
                <OptiBayDiagnosticsTab ticket={ticket} />
              </CommandCenterTabBoundary>
            </TabsContent>
          </ShowFeature>

          <TabsContent value="training" className="flex-1 overflow-hidden mt-0">
            <CommandCenterTabBoundary key={`training-${refreshNonce}`} section="Training" ticketId={ticket.id}>
              {/* Batch 18-K108 activation — Companion install gate ON. A monitored
                  launch first checks (tokenless PING) that the v1.1.0 Training
                  Companion is installed; if not, the install modal is shown
                  instead of silently launching. "Continue without verified
                  monitoring" stays disabled (allowContinueWithout not passed). */}
              <TicketTrainingTab ticket={ticket} ticketJobs={ticketJobs} technicianProfileId={technicianProfileId} companionInstallGate={true} />
            </CommandCenterTabBoundary>
          </TabsContent>

          <ShowFeature feature="ui_ticketPartsPanel">
            <TabsContent value="parts" className="flex-1 overflow-hidden mt-0">
              <CommandCenterTabBoundary key={`parts-${refreshNonce}`} section="Parts" ticketId={ticket.id}>
                <TicketPartsPanel ticketId={ticket.id} />
              </CommandCenterTabBoundary>
            </TabsContent>
          </ShowFeature>

          {/* Core-100 R2 CORE-AA: one ordered history across the ticket's stores, sources named. */}
          <TabsContent value="history" className="flex-1 overflow-hidden mt-0">
            <CommandCenterTabBoundary key={`history-${refreshNonce}`} section="History" ticketId={ticket.id}>
              <TicketReconstructedHistory ticketId={ticket.id} className="h-[calc(85vh-160px)]" />
            </CommandCenterTabBoundary>
          </TabsContent>

          {canViewRankingTab && (
            <TabsContent value="ranking" className="flex-1 overflow-hidden mt-0">
              <CommandCenterTabBoundary key={`ranking-${refreshNonce}`} section="Ranking" ticketId={ticket.id}>
                <TicketRankingPanel ticketId={ticket.id} className="h-[calc(85vh-160px)]" />
              </CommandCenterTabBoundary>
            </TabsContent>
          )}
        </Tabs>

        {/* Combined Customer & Vehicle Edit Modal */}
        <EditCustomerVehicleModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          ticketId={ticket.id}
          initialName={(ticket as any).customer_name || ticket.customerName || ''}
          initialPhone={(ticket as any).customer_phone || ticket.customerPhone || ''}
          initialEmail={(ticket as any).customer_email || ''}
          emailOptIn={(ticket as any).email_opt_in ?? true}
          smsOptIn={(ticket as any).sms_opt_in ?? false}
          preferredChannel={(ticket as any).preferred_channel || 'email'}
          fallbackChannel={(ticket as any).fallback_channel || 'sms'}
          initialYear={vehicle?.year?.toString() || ''}
          initialMake={vehicle?.make || ''}
          initialModel={vehicle?.model || ''}
          initialLicensePlate={vehicle?.licensePlate || ''}
          initialVin={(vehicle as any)?.vin || ''}
          initialEngineSize={vehicleEngineSize}
          initialOdometerMiles={(ticket as any).odometer_miles}
        />

        {/* Edit Ticket Details Modal (type, times, comeback) */}
        <EditTicketDetailsModal
          isOpen={isEditDetailsOpen}
          onClose={() => setIsEditDetailsOpen(false)}
          ticketId={ticket.id}
          initialTicketType={((ticket as any).type || 'Drop-off') as 'Waiter' | 'Drop-off'}
          initialScheduledStartTime={(ticket as any).scheduled_start_time || null}
          initialDueOut={(ticket as any).due_out || null}
          initialIsComeback={(ticket as any).is_comeback || false}
          predictedMinutes={totalMinutes}
        />

        {/* Add Job Dialog */}
        <AddJobDialog
          isOpen={isAddJobOpen}
          onClose={() => setIsAddJobOpen(false)}
          ticketId={ticket.id}
        />
      </DialogContent>
    </Dialog>
  );
};

/**
 * Phase 16 — Inline section showing the ticket's commercial workflow state.
 * Renders a clearly-labeled badge (Three-Status Clarity), a compact transition
 * timeline, and the ready-to-invoice eligibility mirror (server RPC is canonical).
 */
function Phase16BusinessStateSection({ ticketId }: { ticketId: string }) {
  const { data: bizState } = useTicketBusinessState(ticketId);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">Commercial workflow</div>
        <TicketBusinessStateBadge state={bizState?.current_state} showDoctrineLabel />
      </div>
      {bizState?.current_state === 'work_complete' && (
        <ReadyToInvoiceBlockersList ticketId={ticketId} />
      )}
      <TicketBusinessStateTimeline ticketId={ticketId} />
    </div>
  );
}
